import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { useScrollLock } from '@/hooks/useScrollLock';

interface Keyword {
  id: number;
  keyword: string;
  action: 'block' | 'auto_report' | 'auto_hide';
  description: string | null;
  hit_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const ACTION_LABEL: Record<Keyword['action'], string> = {
  block: '차단',
  auto_hide: '자동숨김',
  auto_report: '자동신고',
};

export function KeywordsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'keywords'],
    queryFn: () => get<{ keywords: Keyword[] }>('/admin/keywords'),
  });

  const [editing, setEditing] = useState<Partial<Keyword> | null>(null);
  useScrollLock(editing !== null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const body = {
        keyword: editing.keyword,
        action: editing.action,
        is_active: editing.is_active ?? true,
      };
      if (editing.id) {
        await put(`/admin/keywords/${editing.id}`, body);
      } else {
        await post('/admin/keywords', body);
      }
    },
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['admin', 'keywords'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => del(`/admin/keywords/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'keywords'] }),
  });

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold">키워드 필터</h1>
        <Button onClick={() => setEditing({ action: 'auto_hide', is_active: true })}>
          + 새 키워드
        </Button>
      </div>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        <strong>액션 설명</strong> · 차단(block): 게시 자체 거부 · 자동숨김(auto_hide): 작성은 되지만 즉시 블라인드 · 자동신고(auto_report): 신고 큐에 우선순위로 등록
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left">
              <tr>
                <th className="p-3">키워드</th>
                <th className="p-3">액션</th>
                <th className="p-3">설명</th>
                <th className="p-3">매칭</th>
                <th className="p-3">상태</th>
                <th className="p-3 text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {data?.keywords.map((k) => (
                <tr key={k.id} className="border-t border-[var(--color-border)]">
                  <td className="p-3 font-mono">{k.keyword}</td>
                  <td className="p-3">
                    <Badge
                      variant={
                        k.action === 'block'
                          ? 'fall'
                          : k.action === 'auto_report'
                            ? 'editor'
                            : 'outline'
                      }
                    >
                      {ACTION_LABEL[k.action]}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-[var(--color-text-sub)]">
                    {k.description ?? '-'}
                  </td>
                  <td className="p-3 text-xs">{k.hit_count}회</td>
                  <td className="p-3">
                    {k.is_active ? (
                      <Badge variant="rise">활성</Badge>
                    ) : (
                      <Badge variant="default">비활성</Badge>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(k)}>
                      수정
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      className="ml-1"
                      onClick={() => {
                        if (confirm(`"${k.keyword}" 키워드를 삭제할까요?`)) {
                          deleteMut.mutate(k.id);
                        }
                      }}
                    >
                      삭제
                    </Button>
                  </td>
                </tr>
              ))}
              {data?.keywords.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-[var(--color-text-sub)]">
                    등록된 키워드가 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md space-y-3 rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="text-lg font-bold">
              {editing.id ? '키워드 수정' : '새 키워드 추가'}
            </h3>
            <Input
              placeholder="키워드 (대소문자 구분 없음, lowercase 저장)"
              value={editing.keyword ?? ''}
              onChange={(e) => setEditing({ ...editing, keyword: e.target.value })}
            />
            <select
              value={editing.action ?? 'auto_hide'}
              onChange={(e) =>
                setEditing({ ...editing, action: e.target.value as Keyword['action'] })
              }
              className="h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
            >
              <option value="block">차단 (block)</option>
              <option value="auto_hide">자동숨김 (auto_hide)</option>
              <option value="auto_report">자동신고 (auto_report)</option>
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.is_active ?? true}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
              />
              활성화
            </label>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" fullWidth onClick={() => setEditing(null)}>
                취소
              </Button>
              <Button
                fullWidth
                disabled={!editing.keyword || (editing.keyword?.length ?? 0) < 1}
                loading={saveMut.isPending}
                onClick={() => saveMut.mutate()}
              >
                저장
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
